// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetPrepareTwoStepTransfer } from '@hooks/other/useGetPrepareTwoStepTransfer';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { Typography } from '@mui/material';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import FormDialog from '../../../components/Dialog/FormDialog';
import { ExecuteBefore } from '../../../components/ExecuteBefore';
import TextInput from '../../../components/Form/TextInput';
import { InstrumentSelections } from '../../../components/InstrumentSelections';
import { useParties } from '../../../contexts/PartiesContext';
import TemplateContract from '../../../utils/TemplateContract';
import { getValidateAmountMessage } from '../../../utils/getValidateAmountMessage';
import { MaxLimitErrorMessage } from './MaxLimitErrorMessage';
import { useOfferTransferInstruction } from './useOfferTransferInstruction';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  holding?: TemplateContract<Holding>;
  disableDialog?: boolean;
};

export const OfferTransferDialog = ({ disableDialog, open, onClose, holding }: DialogProps) => {
  const [receiver, setReceiver] = useState<string>('');
  const [instrumentId, setInstrumentId] = useState<string>(holding?.payload.instrument.id || '');
  const [amount, setAmount] = useState<string>(holding?.payload.amount || '');
  const [senderLabel, setSenderLabel] = useState<string>(holding?.payload.label || '');
  const [registrar, setRegistrar] = useState<string>(holding?.payload.registrar || '');
  const [reference, setReference] = useState<string>('');
  const [amountValidationMsg, setAmountValidationMsg] = useState('');
  const prepareTwoStepTransfer = useGetPrepareTwoStepTransfer();
  const { party } = useParties();
  const [hasDateError, setDateError] = useState(false);
  const defaultExecuteBefore = dayjs().add(3, 'day');
  const [executeBefore, setExecuteBefore] = useState(defaultExecuteBefore);
  const onDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) {
      return;
    }
    setDateError(false);
    const isBefore = date.isBefore(dayjs());
    if (isBefore) {
      setDateError(true);
    }
    const now = dayjs();
    const dateWithCurrTime = date
      .set('hour', now.hour())
      .set('minute', now.minute())
      .set('second', now.second());
    setExecuteBefore(dateWithCurrTime);
  };
  const handlePresetDays = (days: number) => {
    setDateError(false);
    const newDate = dayjs().add(days, 'day');
    setExecuteBefore(newDate);
  };
  const [showMaxLimitError, setShowMaxLimitError] = useState(false);
  const onInstrumentChange = (instId: string, instRegistrar: string) => {
    setInstrumentId(instId);
    setRegistrar(instRegistrar);
  };
  const offTransferInstruction = useOfferTransferInstruction();
  const onTransferTransferInstruction = async () => {
    setShowMaxLimitError(false);

    const { holdingCids, exceedsMaxLimit } = prepareTwoStepTransfer({
      instrumentId,
      registrar,
      amount,
      owner: party,
    });
    if (exceedsMaxLimit) {
      setShowMaxLimitError(true);
      return;
    }
    await offTransferInstruction({
      party,
      receiver,
      amount,
      source: registrar,
      instrumentId,
      holdingCids,
      reference,
      executeBefore: executeBefore.toISOString(),
    });
    onClose();
  };

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const getTransferValidationMsg = getValidateAmountMessage('Transfer', holding?.payload.amount);

  const disableSubmit =
    !receiver ||
    !instrumentId ||
    !amount ||
    !registrar ||
    hasDateError ||
    !!amountValidationMsg.length;
  return (
    <FormDialog
      disableDialog={disableDialog}
      open={open}
      title="Offer Transfer"
      onCancel={handleClose}
      onSubmit={onTransferTransferInstruction}
      submitName="Offer"
      isSubmitDisabled={disableSubmit}
    >
      <InstrumentSelections
        instrumentId={instrumentId}
        registrar={registrar}
        onInstrumentChange={onInstrumentChange}
      />

      <TextInput label="Receiver" value={receiver} setValue={setReceiver} required />

      <TextInput
        label="Amount"
        getValidationMessage={getTransferValidationMsg}
        setValidationMessage={setAmountValidationMsg}
        validationMessage={amountValidationMsg}
        value={amount}
        setValue={setAmount}
        required
        type="number"
      />
      <Typography color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Execute Before
      </Typography>
      <ExecuteBefore
        handlePresetDays={handlePresetDays}
        selectedDate={executeBefore}
        onChange={onDateChange}
      />
      {hasDateError && (
        <Typography variant="caption" color="error">
          Date must be in the future
        </Typography>
      )}
      <TextInput
        label="Label"
        value={senderLabel}
        setValue={setSenderLabel}
        disabled={!!holding}
        hidden
      />

      <TextInput label="Reference" value={reference} setValue={setReference} />
      {showMaxLimitError && <MaxLimitErrorMessage />}
    </FormDialog>
  );
};
